/**
 * TaskGenerationLink Component
 *
 * A simple banner that appears on the Sources page when tasks are being generated.
 * Links to the Remediation page where the full status is shown.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export function TaskGenerationLink() {
  const [generating, setGenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Use refs to track polling state without causing re-renders
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollingRef = useRef(false);

  // Fetch status and return whether we should continue polling
  const fetchStatus = useCallback(async (): Promise<boolean> => {
    try {
      const result = await api.getTaskGenerationStatus();
      setGenerating(result.generating);
      setStatusMessage(result.statusMessage);
      return result.generating;
    } catch (error) {
      console.error("[TaskGenerationLink] Failed to fetch status:", error);
      return false;
    }
  }, []);

  // Start or restart polling
  const startPolling = useCallback(async () => {
    // Don't start a new interval if already polling
    if (isPollingRef.current) {
      // But do fetch once to get latest status
      await fetchStatus();
      return;
    }

    isPollingRef.current = true;
    const shouldContinue = await fetchStatus();

    if (shouldContinue) {
      // Clear any existing interval just in case
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Poll every 2 seconds while jobs are pending
      intervalRef.current = setInterval(async () => {
        const stillGenerating = await fetchStatus();
        if (!stillGenerating) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          isPollingRef.current = false;
        }
      }, 2000);
    } else {
      isPollingRef.current = false;
    }
  }, [fetchStatus]);

  // Initial fetch and polling on mount
  useEffect(() => {
    startPolling();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      isPollingRef.current = false;
    };
  }, [startPolling]);

  // Listen for source-labeled events to restart polling
  useEffect(() => {
    function handleLabelApplied() {
      // Start polling (will fetch and start interval if needed)
      startPolling();
    }

    window.addEventListener("source-labeled", handleLabelApplied);
    return () => {
      window.removeEventListener("source-labeled", handleLabelApplied);
    };
  }, [startPolling]);

  if (!generating || !statusMessage) {
    return null;
  }

  return (
    <Link
      to="/alerts?tab=tasks"
      className="block bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 hover:bg-amber-100 transition-colors group"
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <p className="text-sm font-medium text-amber-800">
              {statusMessage}
            </p>
          </div>
          <p className="text-xs text-amber-600 mt-0.5">
            View progress in Action Items
          </p>
        </div>
        <ArrowRight className="w-5 h-5 text-amber-600 group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}
